import { ProductPageData } from "../_types";

export const JSON_PRODUCT_MAJIK_MESSAGE: ProductPageData = {
  id: "majik-message",
  timestamp: new Date("2026-01-30").toISOString(),
  lastUpdate: new Date("2026-05-13").toISOString(),
  version: "1.5.0",
  isEnabled: true,
  repository: {
    url: "https://github.com/Majikah/majik-message",
    owner: "Majikah",
    repo: "majik-message",
  },
  metadata: {
    productName: "Majik Message",
    productSlug: "majik-message",
    tagline: "Post-Quantum Private Messaging Built on Cryptographic Identity",
    description:
      "True end-to-end post-quantum encryption with voice calls, encrypted file sharing, and digital signatures. No phone numbers. No passwords. Just your seed phrase and complete privacy—safe from quantum computers.",
    seo: "Post-quantum end-to-end encryption with ML-KEM-768 and ML-DSA-87. Seed phrase–based accounts. Encrypted voice calls and messages. Encrypted file vault. Auto-expiring messages. Offline-ready. Exportable encrypted messages. Tamper-proof threads with blockchain-like integrity. Quantum-resistant messaging.",
    keywords: [
      "encryption",
      "privacy",
      "messaging",
      "E2EE",
      "post-quantum",
      "ML-KEM",
      "ML-DSA",
      "FIPS-203",
      "FIPS-204",
      "quantum-resistant",
      "seed phrase",
      "cryptographic identity",
      "blockchain",
      "immutable messaging",
      "audit trail",
      "Argon2id",
      "voice calls",
      "encrypted files",
      "digital signatures",
      "file vault",
    ],
    category: "Social",
    subcategory: "Messaging",
    photo: {
      thumbnail:
        "https://storage.majikah.solutions/public/Logo_MajikMessage_Majikah_Thumbnail_WEBP.webp",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikMessage_SocialCard.webp",
      banner:
        "https://storage.majikah.solutions/public/MajikMessage_SmallTile.jpg",
    },
    video: ["0R8ticREPiA"],
  },
  hero: {
    title: "Majik Message",
    subtitle: "Post-Quantum Private Messaging Built on Cryptographic Identity",
    description:
      "True end-to-end post-quantum encryption. Voice calls, encrypted files, and digital signatures. No phone numbers. No passwords. Just your seed phrase and complete privacy—safe from quantum computers.",
    ctaButtons: [
      {
        label: "Download from Microsoft Store",
        url: "#download",
        variant: "primary",
      },
      {
        label: "Open Web App",
        url: "https://message.majikah.solutions",
        variant: "primary",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-message",
        variant: "secondary",
        external: true,
      },
      {
        label: "See Technical Details",
        url: "#technical-specs",
        variant: "tertiary",
      },
      {
        label: "Read Docs",
        url: "/products/majik-message/docs",
        variant: "tertiary",
      },
    ],
  },
  overview: {
    title: "What is Majik Message?",
    paragraphs: [
      "Majik Message is a post-quantum secure messaging platform that replaces traditional username and password accounts with cryptographic identity. Your account *is* your encryption keys—generated from a 12/24-word seed phrase that only you control.",
      "Messages, voice calls, and files are protected end-to-end using ML-KEM-768 (FIPS-203) post-quantum key encapsulation combined with X25519 for identity, AES-256-GCM for content encryption, and hybrid Ed25519 + ML-DSA-87 (FIPS-204) digital signatures for authenticity. This ensures your communications remain secure even against future quantum computers.",
      "Whether you're online or offline, you maintain full control over your encrypted communications—without relying on centralized infrastructure, personal information, or trusted intermediaries. Your messages, files, and calls are quantum-safe: even a powerful quantum computer cannot break the encryption.",
    ],
    highlights: [
      {
        icon: "lock",
        label: "Post-Quantum Encrypted",
        description:
          "ML-KEM-768 (FIPS-203) + ML-DSA-87 (FIPS-204) + AES-256-GCM",
      },
      {
        icon: "key",
        label: "Seed Phrase Identity",
        description: "No email or phone number required",
      },
      {
        icon: "phone",
        label: "P2P Encrypted Voice",
        description: "Direct peer-to-peer encrypted audio calls",
      },
      {
        icon: "wifiSlash",
        label: "Works Offline",
        description: "Encrypt and decrypt without internet",
      },
    ],
  },
  features: [
    {
      id: "post-quantum-encryption",
      title: "Post-Quantum End-to-End Encryption (ML-KEM-768 + X25519)",
      description:
        "Majik Message uses NIST-standardized post-quantum cryptography combined with proven elliptic curve algorithms:",
      list: [
        {
          label: "ML-KEM-768 (FIPS-203)",
          text: "post-quantum key encapsulation mechanism based on lattice cryptography—secure against quantum computer attacks",
        },
        {
          label: "X25519 (Curve25519)",
          text: "generates your unique fingerprint and provides backward compatibility",
        },
        {
          label: "AES-256-GCM",
          text: "encrypts message content with authenticated encryption and tamper detection",
        },
        {
          label: "Argon2id",
          text: "modern memory-hard key derivation (64 MB, 3 iterations) resistant to GPU/ASIC cracking",
        },
      ],
      content: [
        "Every message is encrypted on your device with post-quantum algorithms before transmission and can only be decrypted by the intended recipient. Not even Majik Message servers can access your message content—and neither can a quantum computer.",
        "",
        "**What this means for you:** The encryption protecting your messages today will remain secure even when quantum computers become powerful enough to break traditional encryption methods like RSA and standard elliptic curves.",
      ],
    },
    {
      id: "post-quantum-signatures",
      title: "Post-Quantum Digital Signatures (Ed25519 + ML-DSA-87)",
      description:
        "Majik Message uses hybrid cryptographic signatures to verify the authenticity and integrity of encrypted files and attachments:",
      list: [
        {
          label: "Ed25519",
          text: "classical elliptic curve signature scheme for fast, compact signatures",
        },
        {
          label: "ML-DSA-87 (FIPS-204)",
          text: "NIST-standardized post-quantum digital signature algorithm—resistant to quantum computer attacks",
        },
        {
          label: "Automatic signing",
          text: "all encrypted Majik Files are automatically signed before encryption",
        },
        {
          label: "Public verification",
          text: "anyone can independently verify signed Majik files using the Majik Signature verification tool",
        },
      ],
      content: [
        "Every file you share through Majik Message is cryptographically signed with both Ed25519 and ML-DSA-87 before encryption. This ensures that recipients can verify the file came from you and has not been tampered with—even in a post-quantum world.",
        "",
        "**What this means for you:** Even if an attacker intercepts an encrypted file, they cannot forge your signature or substitute a malicious file without detection.",
      ],
    },
    {
      id: "voice-calls",
      title: "P2P Encrypted Voice Calls",
      description:
        "Majik Message v1.4.1 introduced high-fidelity, encrypted real-time voice communication:",
      list: [
        {
          label: "P2P encrypted audio",
          text: "Real-time voice calls powered by direct peer-to-peer encryption—no intermediate servers handle your audio data",
        },
        {
          label: "Picture-in-Picture (PiP) mode",
          text: "Keep a draggable call overlay visible while navigating other parts of the app",
        },
        {
          label: "Minimized pill notification",
          text: "Collapse an active call into a sleek status indicator while you multitask",
        },
        {
          label: "Real-time audio visualizer",
          text: "High-fidelity visualization provides live feedback for active voice streams",
        },
        {
          label: "Missed call tracking",
          text: "Clear indicators in your chat history ensure you never lose track of a missed connection",
        },
        {
          label: "Custom sonic identity",
          text: "Original dialtones and ringtones produced specifically for the Majikah ecosystem",
        },
      ],
      content: [
        "Voice calls are routed directly between peers—your audio never passes through Majik Message servers. This peer-to-peer architecture provides both privacy and low latency.",
        "",
        "**Coming soon:** Native SFU-powered group calling is currently in development.",
      ],
    },
    {
      id: "encrypted-file-vault",
      title: "Encrypted File Vault & Attachments",
      description:
        "Store and share files securely with end-to-end encryption and post-quantum digital signatures:",
      list: [
        {
          label: "500 MB permanent encrypted storage",
          text: "Store files in your personal encrypted vault, accessible across devices",
        },
        {
          label: "Temporary encrypted storage",
          text: "Share files with up to 15-day expiration for time-sensitive transfers",
        },
        {
          label: "Encrypted chat & thread attachments",
          text: "Send encrypted files and images directly within conversations and threads",
        },
        {
          label: "Locally signed before upload",
          text: "Files are encrypted and cryptographically signed on your device before leaving it",
        },
        {
          label: "Tamper detection",
          text: "Hybrid post-quantum signatures detect any modification to shared files",
        },
        {
          label: "Public verification support",
          text: "Recipients can verify file authenticity using the Majik Signature verification tool",
        },
      ],
      content: [
        "The file vault integrates seamlessly with Majik Message's post-quantum encryption model. All files are encrypted with AES-256-GCM and signed with Ed25519 + ML-DSA-87 before upload—Majik Message servers store only encrypted, signed blobs.",
      ],
    },
    {
      id: "file-security-scanning",
      title: "Built-In File Security Scanning",
      description:
        "Majik Message protects users from malicious uploads with multi-layer scanning applied before encrypted upload:",
      list: [
        {
          label: "YARA malware detection",
          text: "Identifies trojans, malicious executables, and known malware patterns",
        },
        {
          label: "Google Magika AI",
          text: "Detects file format spoofing and disguised files using AI-powered content analysis",
        },
        {
          label: "Broad coverage",
          text: "Scanning applies to chat attachments, file vault uploads, and cloud storage files",
        },
      ],
      content: [
        "Security scanning runs locally on your device before files are encrypted and uploaded. This ensures that even post-quantum–encrypted transfers don't inadvertently carry malicious payloads.",
      ],
    },
    {
      id: "image-messaging",
      title: "Encrypted Image Messaging",
      description:
        "Share images securely within conversations, fully encrypted end-to-end:",
      list: [
        {
          text: "Send encrypted images up to 10 MB and 4000×4000 resolution in chats and threads",
        },
        {
          text: "Inline image rendering with secure local caching for faster repeat viewing",
        },
        {
          text: "Images are encrypted with ML-KEM-768 like all other message content",
        },
        {
          text: "Encrypted image previews embedded directly in message threads",
        },
      ],
      content: [
        "Image data is encrypted on your device before transmission. Recipients decrypt and render images locally—no unencrypted image data ever touches Majik Message servers.",
      ],
    },
    {
      id: "seed-phrase-identity",
      title: "Seed Phrase–Based Hybrid Identity",
      description:
        "Your account is a 12/24-word BIP39 mnemonic seed phrase—the same standard used by cryptocurrency wallets. From this single phrase, two keypairs are derived:",
      list: [
        {
          label: "X25519 keypair (from seed[0..32])",
          text: "provides your unique fingerprint and account ID for identity verification",
        },
        {
          label: "ML-KEM-768 keypair (from full seed[0..64])",
          text: "handles post-quantum message encryption (1184-byte public key, 2400-byte secret key)",
        },
        {
          text: "Both keypairs are deterministically generated—same seed phrase always produces the same keys",
        },
        {
          text: "Your private keys never leave your device and are never transmitted",
        },
        {
          text: "No email, phone number, or personal information required",
        },
      ],
      content: [
        "Your seed phrase is the single source of truth for your account. As long as you have your 12 words, you can recover full access to your identity and decrypt your messages—anywhere, anytime.",
        "",
        "**Automatic Upgrade:** Old accounts are automatically upgraded to post-quantum security when you import your backup. The mnemonic deterministically regenerates both X25519 and ML-KEM-768 keys, and Argon2id re-encrypts them with modern KDF.",
      ],
    },
    {
      id: "offline-operation",
      title: "Works Online and Offline",
      description:
        "Majik Message doesn't require constant connectivity. Your encryption keys work locally:",
      list: [
        {
          label: "Encrypt messages offline",
          text: "Generate quantum-safe encrypted messages on your device without internet access",
        },
        {
          label: "Decrypt messages offline",
          text: "Read previously received messages anytime, even when disconnected",
        },
        {
          label: "Verify identities independently",
          text: "Confirm contact fingerprints using cryptographic verification",
        },
      ],
      content: [
        "This makes Majik Message ideal for high-security environments, air-gapped systems, or situations where network access is restricted or untrusted.",
      ],
    },
    {
      id: "realtime-messaging",
      title: "Realtime Messaging (Free for Everyone)",
      description:
        "When online, Majik Message provides instant post-quantum encrypted messaging:",
      list: [
        {
          text: "WebSocket-based realtime delivery with quantum-safe encryption",
        },
        {
          text: "Messages stored temporarily in Redis with automatic expiration (24 hours by default, expandable to 30 days)",
        },
        {
          text: "Typing indicators and read receipts for active conversations",
        },
        {
          text: "Push notifications for new messages and calls",
        },
        {
          text: "Messages automatically expire and are permanently deleted from servers",
        },
      ],
      content: [
        "Realtime messaging is completely free for all users. Messages remain post-quantum encrypted during transmission and are never permanently stored on Majik Message servers.",
      ],
    },
    {
      id: "group-messaging",
      title: "Group Messaging (Up to 25 Participants)",
      description: "Majik Message supports secure group conversations:",
      list: [
        {
          text: "Create groups with up to 25 participants (including yourself)",
        },
        {
          text: "Each message is individually encrypted using ML-KEM-768 key encapsulation for every group member",
        },
        {
          text: "Same post-quantum security guarantees as one-on-one messaging",
        },
        {
          text: "Typing indicators and read receipts work in group chats",
        },
        {
          text: "Send encrypted images and file attachments in group conversations",
        },
      ],
      content: [
        "**How group encryption works:** A single random AES-256 key encrypts the message once. This key is then individually sealed for each recipient using ML-KEM-768 encapsulation—only the holder of the matching ML-KEM secret key can decrypt their copy.",
      ],
    },
    {
      id: "threads",
      title: "Threads: Tamper-Proof Quantum-Safe Conversations",
      description:
        "Threads are blockchain-like email conversations with cryptographic integrity, post-quantum encryption, and collaborative deletion controls. Only available for registered online accounts.",
      list: [
        {
          label: "Immutable message history",
          text: "Once sent, messages cannot be edited or deleted individually—ensuring complete audit trail integrity",
        },
        {
          label: "SHA3-512 secured chain",
          text: "The entire thread chain is secured with SHA3-512 for stronger integrity guarantees",
        },
        {
          label: "Blockchain-like hashing",
          text: "Each message is cryptographically hashed (SHA-256) and linked to the previous message, creating a tamper-evident chain",
        },
        {
          label: "Encrypted thread subjects",
          text: "Thread subject lines are encrypted to protect conversation metadata",
        },
        {
          label: "Post-quantum encrypted",
          text: "Thread messages use ML-KEM-768 encryption—safe from quantum computer attacks",
        },
        {
          label: "Encrypted file attachments",
          text: "Cryptographic signatures strengthen authenticity of encrypted file attachments within threads",
        },
        {
          label: "Collaborative deletion",
          text: "Threads can only be deleted when ALL participants approve—no single party can unilaterally erase the conversation",
        },
        {
          label: "Built-in thread validation",
          text: "Validate the integrity of any thread chain directly within the app",
        },
        {
          label: "Secure archival",
          text: "Deleted threads remain encrypted on servers for 72 hours with download links sent to all participants via email",
        },
        {
          label: "Read receipts and tracking",
          text: "Track which participants have read each message and monitor thread activity",
        },
        {
          label: "Thread statuses",
          text: "Ongoing, Closed, Pending Deletion, or Marked for Deletion—providing clear conversation state",
        },
      ],
      content: [
        "**How Thread Deletion Works:**",
        "1. Any participant can request deletion of a thread",
        "2. The thread enters 'Pending Deletion' status until all participants approve",
        "3. Once all participants approve, the thread is 'Marked for Deletion'",
        "4. Marked threads are permanently deleted from the server after 72 hours",
        "5. During the 72-hour window, encrypted archives are available for download via email links sent to all participants",
        "",
        "**Thread Integrity Verification:**",
        "Each message in a thread contains:",
        "- A unique hash (SHA-256) of its content, sender, recipients, and timestamp",
        "- A previous hash (p_hash) linking it to the prior message (or thread hash for the first message)",
        "- The full chain is secured with SHA3-512 for stronger collision resistance",
        "- Deletion approval signatures that are cryptographically verified",
        "",
        "This blockchain-like structure makes it cryptographically impossible to tamper with thread history without detection—and the underlying encryption remains quantum-safe.",
      ],
    },
    {
      id: "contact-invitation-system",
      title: "Contact & Invitation System",
      description:
        "Majik Message uses a secure contact exchange model to protect your directory:",
      list: [
        {
          label: "Invitation requests",
          text: "Send invitation requests when messaging users not yet in your contact directory",
        },
        {
          label: "Secure contact card exchange",
          text: "Contact cards (including both X25519 and ML-KEM-768 public keys) are exchanged only upon acceptance",
        },
        {
          label: "Automatic verification",
          text: "Accepted contacts are automatically added to your verified local directory",
        },
        {
          label: "In-chat management",
          text: "Manage pending and received invites directly from Contacts or Chat conversations",
        },
        {
          label: "Fingerprint verification",
          text: "Contacts are identified by SHA-256 hashes of X25519 public keys—no personal data required",
        },
      ],
      content: [
        "The invitation system ensures you only exchange cryptographic identity with people who have explicitly accepted your request. All contact data is stored locally on your device.",
      ],
    },
    {
      id: "message-expiration",
      title: "Message Expiration Timer",
      description: "",
      list: [],
      content: [
        "Set custom expiration times for sensitive conversations. Messages automatically delete after the specified duration, reducing your digital footprint and limiting exposure of confidential information.",
      ],
    },
    {
      id: "encrypted-export",
      title: "Quantum-Safe Encrypted Message Export",
      description:
        "Messages can be exported as encrypted Base64 strings using ML-KEM-768 encryption:",
      list: [
        {
          text: "Download and archive quantum-safe encrypted messages locally",
        },
        {
          text: "Share encrypted content through any channel (email, file storage, etc.)",
        },
        {
          text: "Messages remain fully post-quantum encrypted outside the platform—only decryptable with the correct seed phrase",
        },
      ],
      content: [
        "This allows secure long-term storage and cross-platform portability without compromising encryption—your archived messages will remain safe even when quantum computers become mainstream.",
      ],
    },
    {
      id: "solo-messages",
      title: "Solo Messages for Personal Storage",
      description:
        "Encrypt messages where you are the only recipient—perfect for private notes and journals:",
      list: [
        {
          text: "Sender-only encryption for personal storage and archival",
        },
        {
          text: "Works entirely offline in local mode (Message tab)",
        },
        {
          text: "Perfect for encrypted journals, notes, passwords, or sensitive information",
        },
        {
          text: "Export as quantum-safe encrypted Base64 strings for backup or transfer",
        },
        {
          text: "Not available in realtime chat—requires at least 2 participants",
        },
      ],
      content: [
        "Solo messages use the same ML-KEM-768 post-quantum encryption as group messages but with only yourself as the recipient. This provides a quantum-resistant way to store sensitive information that only you can decrypt.",
      ],
    },
    {
      id: "multi-account",
      title: "Multi-Account Support",
      description:
        "Manage multiple cryptographic identities for different contexts:",
      table: {
        headers: ["Account Type", "Local Storage", "Online Registration"],
        rows: [
          {
            cells: ["Free Users", "Up to 25 accounts", "5 accounts"],
          },
          {
            cells: ["Paid Users", "Up to 25 accounts", "10 accounts"],
          },
        ],
      },
      content: [
        "**What this means:**",
        "- **Local accounts** can encrypt and decrypt messages offline but cannot send/receive realtime messages or use Threads",
        "- **Registered accounts** have full access to realtime messaging, voice calls, Threads, typing indicators, and online features",
        "- You can swap which accounts are registered online at any time",
        "",
        "**How to register an account online:**",
        "1. From the Accounts tab: Hover over an account and click 'Register Online' in the action menu",
        "2. From the Majikah tab: Find 'Registered Identities' section and click the Plus (+) icon to register an existing local account",
        "",
        "**Note:** Registration is only needed for realtime chat, voice calls, and Threads. Local encryption/decryption works without registration.",
      ],
    },
    {
      id: "chrome-extension",
      title: "Chrome Extension",
      description:
        "The Majik Message Chrome extension is available on the Google Chrome Web Store and provides:",
      list: [
        {
          label: "Browser-based post-quantum encryption and decryption",
          text: "Encrypt and decrypt messages directly from your browser using the same seed phrase identity with ML-KEM-768 security",
        },
        {
          label: "DOM scanning",
          text: "Automatically detect encrypted messages (~*$MJKMSG: format) on any webpage and decrypt them inline",
        },
        {
          label: "Offline-only operation",
          text: "The extension does not have access to realtime messaging; it's designed for local encryption/decryption workflows",
        },
      ],
      content: [
        "This enables seamless quantum-safe encrypted communication across web platforms without leaving sensitive data exposed in plain text.",
      ],
    },
    {
      id: "emoji-gif-support",
      title: "Emoji and GIF Support (via GIPHY)",
      description:
        "Express yourself with rich media while maintaining post-quantum security:",
      list: [
        {
          label: "Native emoji picker",
          text: "Full emoji support with integrated picker for quick reactions and expressions",
        },
        {
          label: "GIPHY integration",
          text: "Search and send GIFs directly from the chat interface powered by GIPHY API",
        },
        {
          label: "Encrypted transmission",
          text: "GIF URLs and emoji are encrypted with ML-KEM-768 like all other message content",
        },
        {
          label: "Works in realtime chat",
          text: "Send emojis and GIFs in both individual and group conversations",
        },
      ],
      content: [
        "Make your quantum-safe conversations more expressive. Emoji and GIF metadata is encrypted end-to-end—only your intended recipients can see what you shared.",
      ],
    },
    {
      id: "contact-directory",
      title: "Secure Contact Directory",
      description: "Manage contacts using cryptographic identities:",
      list: [
        {
          text: "Contacts are identified by verified public key fingerprints (SHA-256 hashes of X25519 public keys)",
        },
        {
          text: "Contact cards include both X25519 and ML-KEM-768 public keys for quantum-safe messaging",
        },
        {
          text: "Share contact information via invite keys—one-click generation and import",
        },
        {
          text: "Contacts are automatically checked for online registration status",
        },
        {
          text: "No phone numbers, emails, or personal data required",
        },
        {
          text: "All contact data stored locally on your device",
        },
      ],
      content: [
        "This ensures communication remains private, identity-based, and verifiable through cryptography rather than centralized directories—with post-quantum security built in.",
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Post-Quantum Cryptography Stack",
        table: {
          headers: ["Component", "Implementation"],
          rows: [
            {
              cells: [
                "Post-Quantum Key Encapsulation",
                "ML-KEM-768 (FIPS-203, NIST standardized)",
              ],
            },
            {
              cells: ["Identity Fingerprint", "X25519 (Curve25519) public key"],
            },
            {
              cells: [
                "Symmetric Encryption",
                "AES-256-GCM (authenticated encryption)",
              ],
            },
            {
              cells: [
                "Digital Signatures",
                "Hybrid Ed25519 + ML-DSA-87 (FIPS-204, NIST standardized)",
              ],
            },
            { cells: ["Hash Function (Messages)", "SHA-256"] },
            { cells: ["Hash Function (Thread Chain)", "SHA3-512"] },
            {
              cells: [
                "Key Derivation (Passphrase)",
                "Argon2id (64 MB memory, 4 threads, 3 iterations)",
              ],
            },
            {
              cells: [
                "Key Derivation (Mnemonic)",
                "Argon2id (32 MB memory, 4 threads, 3 iterations)",
              ],
            },
            { cells: ["Mnemonic Standard", "BIP39 (12/24-word seed phrases)"] },
            {
              cells: [
                "Random Number Generation",
                "Browser crypto.getRandomValues (CSPRNG)",
              ],
            },
            {
              cells: [
                "Fingerprint",
                "SHA-256 hash of X25519 public key (Base64-encoded)",
              ],
            },
            {
              cells: [
                "ML-KEM Key Sizes",
                "Public: 1184 bytes, Secret: 2400 bytes, Ciphertext: 1088 bytes",
              ],
            },
          ],
        },
        description:
          "**Post-quantum security:** ML-KEM-768 (Module-Lattice-Based Key Encapsulation Mechanism) is a NIST-standardized algorithm (FIPS-203) based on lattice cryptography. ML-DSA-87 (FIPS-204) provides post-quantum digital signatures. All cryptographic operations use the **@stablelib** library suite and **@noble/post-quantum** for consistent, auditable implementations across platforms.",
      },
      {
        title: "Hybrid Key Derivation",
        table: {
          headers: ["Seed Slice", "Derivation", "Purpose"],
          rows: [
            {
              cells: [
                "seed[0..32]",
                "Ed25519 → X25519 via ed2curve",
                "Account fingerprint & identity",
              ],
            },
            {
              cells: [
                "seed[0..64]",
                "ML-KEM-768 keygen",
                "Post-quantum message encryption",
              ],
            },
          ],
        },
        description:
          "**How it works:** A 12/24-word BIP39 mnemonic generates a 64-byte seed. The first 32 bytes derive an Ed25519 keypair (converted to X25519 for encryption), which provides your fingerprint. The full 64 bytes derive an ML-KEM-768 keypair for post-quantum message encryption. Both keypairs are deterministic—same mnemonic always produces the same keys. This hybrid approach provides backward compatibility (X25519 fingerprints) while adding quantum resistance (ML-KEM-768 encryption).",
      },
      {
        title: "Envelope Format v3 (ML-KEM Only)",
        table: {
          headers: ["Format", "Structure"],
          rows: [
            {
              cells: [
                "Binary envelope",
                "[1-byte version=3][32-byte fingerprint][JSON payload]",
              ],
            },
            {
              cells: ["Solo payload", "{ iv, ciphertext, mlKemCipherText }"],
            },
            {
              cells: [
                "Group payload",
                "{ iv, ciphertext, keys: [{ fingerprint, mlKemCipherText, encryptedAesKey }] }",
              ],
            },
            {
              cells: [
                "Scanner string",
                "~*$MJKMSG:<base64 of binary envelope>",
              ],
            },
          ],
        },
        description:
          "**v3 envelope (current):** Uses ML-KEM-768 key encapsulation exclusively. Legacy v1/v2 envelopes (X25519 ephemeral key exchange) have been fully deprecated. Solo messages use the ML-KEM shared secret directly as the AES key. Group messages use a random AES key sealed per-recipient via ML-KEM encapsulation (aesKey XOR mlKemSharedSecret one-time-pad construction).",
      },
      {
        title: "Thread Architecture",
        table: {
          headers: ["Component", "Implementation"],
          rows: [
            { cells: ["Thread ID", "UUID v4"] },
            {
              cells: [
                "Message Hash",
                "SHA-256 (ID + Message + Sender + Recipients + Timestamp)",
              ],
            },
            {
              cells: [
                "Previous Hash (p_hash)",
                "SHA-256 (Current Hash + Previous Hash)",
              ],
            },
            {
              cells: ["Chain Integrity Hash", "SHA3-512 (full thread chain)"],
            },
            {
              cells: ["Thread Subject", "Encrypted (ML-KEM-768)"],
            },
            {
              cells: [
                "Deletion Approval Hash",
                "SHA-256 (Public Key + Thread ID + Timestamp)",
              ],
            },
            {
              cells: [
                "Thread Statuses",
                "Ongoing, Closed, Pending Deletion, Marked for Deletion",
              ],
            },
            {
              cells: [
                "Archive Retention",
                "72 hours (ML-KEM encrypted, server-side)",
              ],
            },
            { cells: ["Access Control", "Registered online accounts only"] },
            { cells: ["Maximum Message Length", "100,000 characters"] },
            {
              cells: ["Encryption", "ML-KEM-768 v3 envelopes (quantum-safe)"],
            },
            {
              cells: [
                "File Attachment Signatures",
                "Hybrid Ed25519 + ML-DSA-87 (FIPS-204)",
              ],
            },
          ],
        },
        description:
          "**Thread Integrity Model:** Each message in a thread is cryptographically linked to the previous message through SHA-256 hash chaining. The entire chain is further secured with SHA3-512. The first message links to the thread's hash, creating an unbroken chain. Any attempt to modify a message breaks the chain and is immediately detectable through hash verification. **All thread messages use ML-KEM-768 post-quantum encryption.**",
      },
      {
        title: "File Security",
        table: {
          headers: ["Component", "Implementation"],
          rows: [
            {
              cells: [
                "File Encryption",
                "AES-256-GCM (encrypted before upload)",
              ],
            },
            {
              cells: [
                "File Signatures",
                "Hybrid Ed25519 + ML-DSA-87 (FIPS-204)",
              ],
            },
            {
              cells: [
                "Malware Scanning",
                "YARA rules (trojans, malicious executables)",
              ],
            },
            {
              cells: [
                "File Type Verification",
                "Google Magika AI (format spoofing detection)",
              ],
            },
            {
              cells: ["Permanent Storage", "500 MB encrypted vault per user"],
            },
            {
              cells: ["Temporary Storage", "Up to 15-day expiration"],
            },
            {
              cells: [
                "Image Messaging",
                "Up to 10 MB, max 4000×4000 resolution",
              ],
            },
          ],
        },
        description:
          "**File security model:** All files are scanned, signed, and encrypted locally on your device before upload. Majik Message servers store only encrypted, signed blobs—they cannot access file content. Hybrid post-quantum signatures (Ed25519 + ML-DSA-87) ensure long-term authenticity even against quantum computers.",
      },
      {
        title: "Platform & Infrastructure",
        table: {
          headers: ["Component", "Technology"],
          rows: [
            {
              cells: [
                "Realtime Messaging",
                "WebSocket (quantum-safe encrypted)",
              ],
            },
            {
              cells: [
                "Voice Calls",
                "P2P encrypted audio (no intermediate servers)",
              ],
            },
            {
              cells: [
                "Message Storage",
                "Redis with TTL (24h default, max 30 days)",
              ],
            },
            {
              cells: [
                "Thread Storage",
                "Server-side (ML-KEM encrypted, permanent until deletion)",
              ],
            },
            {
              cells: [
                "Data Persistence",
                "No permanent server-side storage for realtime messages",
              ],
            },
            {
              cells: [
                "Local Storage",
                "IndexedDB (Argon2id-encrypted private keys, contacts)",
              ],
            },
            { cells: ["Desktop App", "Microsoft Store, GitHub Releases"] },
            {
              cells: ["Browser Extension", "Google Chrome (Chrome Web Store)"],
            },
            { cells: ["Web App", "https://message.majikah.solutions"] },
          ],
        },
        description:
          "**Important:** Realtime messages are automatically deleted from Redis after expiration. Threads are stored server-side (ML-KEM encrypted) until collaborative deletion is approved. Voice call audio is routed peer-to-peer and never touches Majik Message servers. Majik Message servers cannot decrypt message or file content in any case.",
      },
      {
        title: "Messaging Capabilities",
        table: {
          headers: ["Feature", "Status"],
          rows: [
            { cells: ["Text Messages", "✓ Supported (post-quantum)"] },
            {
              cells: ["Group Chats", "✓ Up to 25 participants (post-quantum)"],
            },
            {
              cells: ["Encrypted Image Messaging", "✓ Up to 10 MB, 4000×4000"],
            },
            {
              cells: [
                "Encrypted File Attachments",
                "✓ Supported (signed + encrypted)",
              ],
            },
            { cells: ["Encrypted File Vault", "✓ 500 MB permanent storage"] },
            { cells: ["P2P Encrypted Voice Calls", "✓ Supported"] },
            { cells: ["Group Voice Calling (SFU)", "⧗ In development"] },
            {
              cells: [
                "Threads (Immutable Email)",
                "✓ Supported (post-quantum)",
              ],
            },
            {
              cells: ["Thread Collaborative Deletion", "✓ Supported"],
            },
            {
              cells: ["Thread Hash Chain Verification", "✓ SHA-256 + SHA3-512"],
            },
            {
              cells: ["Encrypted Thread Subjects", "✓ Supported"],
            },
            {
              cells: [
                "Post-Quantum Digital Signatures",
                "✓ Ed25519 + ML-DSA-87",
              ],
            },
            { cells: ["Push Notifications", "✓ Supported"] },
            { cells: ["Emoji Support", "✓ Native emoji picker"] },
            { cells: ["GIF Support", "✓ GIPHY integration"] },
            { cells: ["Typing Indicators", "✓ Supported"] },
            { cells: ["Read Receipts", "✓ Supported"] },
            { cells: ["Message Expiration", "✓ Custom timers available"] },
            { cells: ["Video Calling", "⧗ Planned"] },
          ],
        },
      },
    ],
  },
  howItWorks: {
    title: "How Majik Message Works",
    processes: [
      {
        title: "Account Creation (Hybrid Keypair Derivation)",
        description: "When you create a Majik Message account:",
        steps: [
          {
            text: "A 12/24-word BIP39 mnemonic seed phrase is generated using cryptographically secure random number generation (CSPRNG)",
            type: "numbered",
          },
          {
            text: "The mnemonic is converted to a 64-byte seed via BIP39 PBKDF2-SHA512",
            type: "numbered",
          },
          {
            text: "Two keypairs are deterministically derived from this seed:",
            type: "numbered",
          },
          {
            text: "• X25519 keypair: seed[0..32] → Ed25519 → X25519 via ed2curve (used for fingerprint & identity)",
            type: "bullet",
          },
          {
            text: "• ML-KEM-768 keypair: seed[0..64] → ml_kem768.keygen() (used for post-quantum message encryption)",
            type: "bullet",
          },
          {
            text: "Your X25519 public key is hashed (SHA-256) to create your account fingerprint",
            type: "numbered",
          },
          {
            text: "Both private keys are encrypted with your passphrase using Argon2id (64 MB memory, 4 threads, 3 iterations) and stored locally in IndexedDB",
            type: "numbered",
          },
        ],
        note: "**Critical security note:** Your seed phrase and private keys never leave your device. The passphrase you set protects your Argon2id-encrypted private keys in local storage. Old accounts are automatically upgraded to Argon2id + ML-KEM when you import your backup—the mnemonic deterministically regenerates all keys.",
      },
      {
        title: "Sending a Post-Quantum Encrypted Message (Solo)",
        description: "When you send a solo message (sender-only):",
        steps: [
          {
            text: "ML-KEM-768 encapsulation is performed on your own ML-KEM public key",
            type: "numbered",
          },
          {
            text: "This generates a 32-byte shared secret + 1088-byte ciphertext",
            type: "numbered",
          },
          {
            text: "The 32-byte shared secret is used directly as the AES-256-GCM key",
            type: "numbered",
          },
          {
            text: "Your message is encrypted with AES-256-GCM using the shared secret and a random 12-byte IV",
            type: "numbered",
          },
          {
            text: "The encrypted message, IV, and ML-KEM ciphertext are packaged into a v3 envelope and stored locally",
            type: "numbered",
          },
        ],
        note: "**Solo messages** are only available in local encryption mode (Message tab). Realtime chat requires at least 2 participants. **Quantum-safe:** Even with a quantum computer, an attacker cannot decrypt this message without your ML-KEM secret key.",
      },
      {
        title: "Group Message Encryption (Post-Quantum)",
        description: "For group messages (2+ recipients including yourself):",
        steps: [
          {
            text: "A random 32-byte AES key is generated for the message",
            type: "numbered",
          },
          {
            text: "The message is encrypted once with AES-256-GCM using this key",
            type: "numbered",
          },
          {
            text: "For each recipient: ML-KEM-768 encapsulation generates a 32-byte shared secret",
            type: "numbered",
          },
          {
            text: "The group AES key is XORed with the shared secret (one-time pad): encryptedAesKey = aesKey XOR mlKemSharedSecret",
            type: "numbered",
          },
          {
            text: "Each recipient gets their own 1088-byte ML-KEM ciphertext and encryptedAesKey in the envelope",
            type: "numbered",
          },
        ],
        note: "This ensures only authorized recipients can decrypt the message—and the ML-KEM encapsulation prevents quantum computer attacks. Even if one recipient's key is compromised, other recipients remain secure.",
      },
      {
        title: "Encrypted File Signing and Upload",
        description: "When you share a file through Majik Message:",
        steps: [
          {
            text: "The file is scanned locally using YARA malware detection and Google Magika AI for format verification",
            type: "numbered",
          },
          {
            text: "A hybrid Ed25519 + ML-DSA-87 signature is generated from the file content and your identity keys",
            type: "numbered",
          },
          {
            text: "The file and its signature are encrypted together with AES-256-GCM",
            type: "numbered",
          },
          {
            text: "The encrypted, signed blob is uploaded to Majik Message servers",
            type: "numbered",
          },
          {
            text: "Recipients decrypt the blob and verify the signature against your known public keys before opening",
            type: "numbered",
          },
        ],
        note: "**Tamper detection:** If a file is modified after signing, signature verification will fail and the recipient is warned. The hybrid signature ensures authenticity even when quantum computers become mainstream.",
      },
      {
        title: "P2P Encrypted Voice Calls",
        description: "When you initiate or receive a voice call:",
        steps: [
          {
            text: "A direct peer-to-peer connection is established between the two devices",
            type: "numbered",
          },
          {
            text: "Audio data is encrypted end-to-end in the P2P channel—no audio passes through Majik Message servers",
            type: "numbered",
          },
          {
            text: "If the app is navigated away from during a call, Picture-in-Picture mode keeps a draggable overlay visible",
            type: "numbered",
          },
          {
            text: "The call can be minimized to a Pill notification for full multitasking",
            type: "numbered",
          },
          {
            text: "Missed calls are tracked and displayed in chat history",
            type: "numbered",
          },
        ],
        note: "**Coming soon:** SFU-powered group calling is currently in development, which will enable encrypted conference calls with multiple participants.",
      },
      {
        title: "Thread Creation and Message Chaining",
        description: "When you create a thread and send messages:",
        steps: [
          {
            text: "A thread is created with a unique UUID and an initial hash derived from the thread ID, owner, participants, and timestamp",
            type: "numbered",
          },
          {
            text: "The thread subject is encrypted with ML-KEM-768 to protect conversation metadata",
            type: "numbered",
          },
          {
            text: "Each message is encrypted with ML-KEM-768 v3 envelope format (post-quantum secure)",
            type: "numbered",
          },
          {
            text: "Each message is hashed (SHA-256) using its ID, content, sender, recipients, and timestamp, and linked to the previous message via p_hash",
            type: "numbered",
          },
          {
            text: "The full thread chain is additionally secured with SHA3-512 for stronger integrity guarantees",
            type: "numbered",
          },
          {
            text: "File attachments within threads are signed with Ed25519 + ML-DSA-87 before encryption",
            type: "numbered",
          },
          {
            text: "When deletion is requested, an approval hash is generated for each participant using their public key, thread ID, and approval timestamp",
            type: "numbered",
          },
        ],
        note: "**Tamper Detection:** If any message is modified after creation, its hash will no longer match the expected value, and the p_hash of the next message will also break. The SHA3-512 chain hash provides an additional integrity layer. **Quantum-Safe:** The underlying encryption uses ML-KEM-768.",
      },
      {
        title: "Receiving and Decrypting a Post-Quantum Message",
        description: "When you receive an encrypted message:",
        steps: [
          {
            text: "Your device retrieves the v3 encrypted envelope from the server or extracts it from the DOM (if using the browser extension)",
            type: "numbered",
          },
          {
            text: "The message fingerprint identifies which of your accounts should decrypt it",
            type: "numbered",
          },
          {
            text: "Your private keys are unlocked by decrypting them with your passphrase via Argon2id",
            type: "numbered",
          },
          {
            text: "For solo messages: Your ML-KEM-768 secret key decapsulates the 1088-byte ciphertext, recovering the 32-byte shared secret",
            type: "numbered",
          },
          {
            text: "For group messages: Your ML-KEM secret key decapsulates your ciphertext, then the shared secret is XORed with your encryptedAesKey to recover the group AES key",
            type: "numbered",
          },
          {
            text: "The message is decrypted using AES-256-GCM with the recovered key and the provided IV",
            type: "numbered",
          },
          {
            text: "If the message was compressed, it's automatically decompressed before display",
            type: "numbered",
          },
        ],
        note: "**Quantum-safe decryption:** ML-KEM decapsulation never throws on the wrong key—it returns garbage, which causes AES-GCM authentication to fail. If decryption fails (due to tampering, incorrect keys, or quantum attack attempts), an authentication error is thrown and the message is rejected.",
      },
      {
        title: "Offline Encryption/Decryption",
        description:
          "Because all cryptographic operations happen locally on your device:",
        steps: [
          {
            text: "You can encrypt messages with post-quantum security without an internet connection",
            type: "bullet",
          },
          {
            text: "You can decrypt previously received messages offline",
            type: "bullet",
          },
          {
            text: "Quantum-safe encrypted messages can be exported as Base64 strings and shared through any medium (email, USB, etc.)",
            type: "bullet",
          },
        ],
        note: "This makes Majik Message ideal for air-gapped systems, high-security environments, or situations where you need quantum-resistant encrypted communication but lack reliable network access.",
      },
    ],
  },
  pricing: {
    title: "Pricing",
    model: "free",
    currentStatus:
      "Majik Message is currently **free for all users**. Realtime messaging, voice calls, post-quantum encryption, and all core features are available at no cost.",
    futureStatus:
      "**Coming soon:** Paid subscription and pay-as-you-go models with expanded account limits, increased file vault storage, and additional features will be available in the future.",
  },
  security: {
    title: "Security Considerations",
    categories: [
      {
        title: "What Majik Message Protects",
        items: [
          {
            label: "Message content:",
            description:
              "Post-quantum encrypted with ML-KEM-768 + AES-256-GCM—safe from quantum computers",
          },
          {
            label: "File integrity:",
            description:
              "Hybrid Ed25519 + ML-DSA-87 (FIPS-204) signatures verify authenticity and detect tampering",
          },
          {
            label: "Voice call audio:",
            description:
              "P2P encrypted audio—no audio data passes through Majik Message servers",
          },
          {
            label: "Identity privacy:",
            description: "No phone numbers or email addresses required",
          },
          {
            label: "Private keys:",
            description:
              "Never transmitted; encrypted at rest with Argon2id (64 MB memory-hard, GPU-resistant)",
          },
          {
            label: "Future-proof security:",
            description:
              "ML-KEM-768 (FIPS-203) and ML-DSA-87 (FIPS-204) ensure messages and signatures remain secure even when quantum computers become powerful",
          },
          {
            label: "Forward secrecy:",
            description:
              "Each message uses fresh ML-KEM encapsulation—past messages remain secure even if current keys are compromised",
          },
        ],
      },
      {
        title: "What Users Must Protect",
        items: [
          {
            label: "Your 12/24-word seed phrase:",
            description:
              "This is the ONLY way to recover your account. If lost, your account and messages are permanently inaccessible. Store it securely offline.",
          },
          {
            label: "Your passphrase:",
            description:
              "Protects your Argon2id-encrypted private keys in local storage. Choose a strong, unique passphrase.",
          },
          {
            label: "Device security:",
            description:
              "If your device is compromised while your account is unlocked, an attacker could access your private keys.",
          },
        ],
      },
      {
        title: "What Majik Message Does Not Protect",
        items: [
          {
            label: "Metadata:",
            description:
              "Timing, message frequency, and participant relationships may be visible to servers or network observers",
          },
          {
            label: "IP addresses:",
            description:
              "Your IP address is visible to Majik Message servers when you connect for realtime messaging; for P2P voice calls, your IP may be visible to your call peer",
          },
          {
            label: "Device compromise:",
            description:
              "If malware or an attacker gains access to your unlocked device, they may access decrypted messages or private keys",
          },
        ],
      },
      {
        title: "Thread Integrity and Deletion",
        items: [
          {
            label: "Hash chain integrity:",
            description:
              "Each message is cryptographically linked to the previous message via SHA-256. The full chain is further secured with SHA3-512. Any tampering breaks the chain and is detectable.",
          },
          {
            label: "Post-quantum thread encryption:",
            description:
              "All thread messages use ML-KEM-768 encryption—archived threads remain secure against quantum computers.",
          },
          {
            label: "Encrypted thread subjects:",
            description:
              "Thread subject lines are encrypted, preventing metadata leakage about conversation topics.",
          },
          {
            label: "Collaborative deletion protection:",
            description:
              "No single participant can delete a thread. All participants must approve deletion, preventing unilateral evidence destruction.",
          },
          {
            label: "72-hour archival window:",
            description:
              "Deleted threads remain available as quantum-safe encrypted archives for 72 hours, giving all participants time to download records.",
          },
          {
            label: "Deletion approval verification:",
            description:
              "Each deletion approval is cryptographically signed and verified to prevent forgery.",
          },
          {
            label: "Immutability:",
            description:
              "Messages in threads cannot be edited. This ensures complete audit trail integrity but means users cannot correct mistakes.",
          },
        ],
      },
      {
        title: "Post-Quantum Security Guarantees",
        items: [
          {
            label: "NIST-standardized encryption:",
            description:
              "ML-KEM-768 (FIPS-203) is a U.S. government standard for post-quantum key encapsulation",
          },
          {
            label: "NIST-standardized signatures:",
            description:
              "ML-DSA-87 (FIPS-204) is a U.S. government standard for post-quantum digital signatures",
          },
          {
            label: "Lattice-based cryptography:",
            description:
              "Based on mathematical problems (Module Learning With Errors) that are believed to be hard for both classical and quantum computers",
          },
          {
            label: "Conservative security level:",
            description:
              "ML-KEM-768 provides security equivalent to AES-192—strong enough to resist even large-scale quantum computers",
          },
          {
            label: "Future-proof:",
            description:
              "Your messages encrypted today will remain secure even when quantum computers become mainstream",
          },
        ],
      },
    ],
  },
  useCases: [
    {
      title: "Privacy-Conscious Individuals",
      description:
        "If you want quantum-safe secure messaging without linking your phone number or email address, Majik Message provides true anonymity. Your identity is your cryptographic keys—nothing more. Your messages will remain private even in a post-quantum world.",
    },
    {
      title: "Future-Proof Security for High-Value Communications",
      description:
        "Governments, militaries, and intelligence agencies are already preparing for quantum computers. If your communications need to remain secure for decades (state secrets, long-term business strategies, sensitive research), Majik Message's ML-KEM-768 encryption and ML-DSA-87 signatures protect against 'harvest now, decrypt later' attacks.",
    },
    {
      title: "Journalists",
      description:
        "Communicate with sources securely with quantum-resistant encryption and P2P voice calls. The offline encryption capability allows you to exchange encrypted messages through air-gapped systems or untrusted networks without exposing sensitive information—and the encryption will remain strong even when quantum computers arrive.",
    },
    {
      title: "Legal, Compliance, and Audit-Required Communications",
      description:
        "Threads provide immutable, tamper-proof records of conversations with post-quantum encryption and cryptographically signed file attachments—ideal for legal discovery, regulatory compliance, contract negotiations, or any situation requiring verified audit trails. Encrypted thread subjects protect metadata. The blockchain-like SHA-256 + SHA3-512 hash chain ensures no party can alter history, while collaborative deletion prevents unilateral evidence destruction.",
    },
    {
      title: "Professionals Handling Sensitive Data",
      description:
        "Lawyers, healthcare providers, researchers, and other professionals can use Majik Message to communicate confidentially with quantum-safe encryption, share verified encrypted files, and conduct private voice calls—without relying on third-party platforms that may store metadata or message content.",
    },
    {
      title: "Security Researchers and Cryptography Professionals",
      description:
        "Majik Message's cryptographic implementation is transparent and uses well-audited libraries (@stablelib, @noble/post-quantum). It's suitable for technical users who want verifiable, inspectable post-quantum encryption and signatures based on NIST standards (FIPS-203, FIPS-204).",
    },
    {
      title: "Anyone Seeking Digital Autonomy and Future-Proof Privacy",
      description:
        "If you believe your communications should be private by default—now and in the future—and you want full control over your identity and data, Majik Message is designed for you. Messages, calls, and files are quantum-safe: even a powerful quantum computer cannot break the encryption.",
    },
  ],
  gettingStarted: {
    title: "Getting Started",
    steps: [
      {
        title: "Download and Install",
        items: [
          "Download from Microsoft Store (Windows) or GitHub Releases (all platforms)",
          "Or open the Web App at message.majikah.solutions for browser-based access",
          "Or install the Chrome extension from the Chrome Web Store if you only need local encryption/decryption without realtime chat",
        ],
      },
      {
        title: "Create Your Account",
        items: [
          "Launch Majik Message",
          "Generate a new 12/24-word seed phrase (or import an existing one)",
          "Set a strong passphrase to encrypt your local private key storage with Argon2id",
          "Both X25519 and ML-KEM-768 keypairs are automatically derived from your seed",
        ],
      },
      {
        title: "Backup Your Account",
        items: [
          "Upon account creation, your backup file will automatically download as a JSON file",
          "Keep this JSON file secure—it's the ONLY way to recover your account if needed",
          "Your backup is encrypted with Argon2id and can fully restore both keypairs",
          "If you update to v1.5.0 and notice local data was cleared, simply reload your Majik Key and re-import your saved contact cards",
        ],
      },
      {
        title: "Register for Realtime Messaging, Calls, and Threads (Optional)",
        items: [
          "To use realtime chat, voice calls, and Threads, register your account online from the Accounts tab (hover and select 'Register Online')",
          "Or register from the Majikah tab under 'Registered Identities' by clicking the Plus icon",
          "Free users can register up to 5 accounts online (store up to 25 locally)",
          "Skip this step if you only need local encryption/decryption",
        ],
      },
      {
        title: "Add Contacts",
        items: [
          "Generate an invite key for your account (one-click) or send an invitation request to another user",
          "Share your invite key with contacts, or import theirs to add them",
          "Accepted invitations automatically exchange contact cards and add verified contacts to your local directory",
          "Contacts registered after the ML-KEM upgrade will automatically have both X25519 and ML-KEM-768 public keys",
          "Contacts must be registered online for realtime messaging, voice calls, and Threads",
        ],
      },
      {
        title: "Start Messaging with Post-Quantum Security",
        items: [
          "For realtime chat: Use the 'Chats' tab (requires at least 2 participants including yourself)—all messages use ML-KEM-768 encryption",
          "For voice calls: Initiate a call from an active chat conversation",
          "For local encryption: Use the 'Message' tab to encrypt quantum-safe messages offline and share through any channel",
          "Pro tip: You can encrypt solo messages (only yourself as recipient) for personal storage like journals or notes—available only in local mode",
        ],
      },
      {
        title: "Share Encrypted Files",
        items: [
          "Attach files directly in chat or thread conversations",
          "Files are automatically scanned, signed with Ed25519 + ML-DSA-87, and encrypted before upload",
          "Use the File Vault for permanent encrypted storage (500 MB) or temporary sharing (up to 15 days)",
          "Recipients can verify file authenticity using the Majik Signature verification tool",
        ],
      },
      {
        title: "Using Threads (Advanced Feature)",
        items: [
          "Navigate to the 'Threads' tab in Majik Message",
          "Create a new thread with an encrypted subject line and invite registered participants",
          "Send quantum-safe encrypted messages and file attachments—remember, messages cannot be edited once sent",
          "Monitor thread status: Ongoing, Closed, Pending Deletion, or Marked for Deletion",
          "Use the built-in thread validation tool to verify chain integrity at any time",
          "Request deletion (if needed): All participants must approve before the thread is deleted",
          "Download archived threads during the 72-hour retention window if needed—archives remain ML-KEM encrypted",
        ],
      },
    ],
  },
  roadmap: {
    title: "Roadmap & Future Features",
    description:
      "Majik Message is under active development. Planned and upcoming features include:",
    items: [
      {
        label: "SFU-powered group voice calling",
        description:
          "Native server-facilitated group calls with end-to-end encryption for multiple participants",
        status: "in-progress",
      },
      {
        label: "Mobile apps",
        description:
          "Native iOS and Android applications with full ML-KEM-768 and ML-DSA-87 support",
        status: "planned",
      },
      {
        label: "Video calling",
        description:
          "Post-quantum encrypted video calls building on the P2P audio infrastructure",
        status: "planned",
      },
      {
        label: "Paid tiers",
        description:
          "Subscription and pay-as-you-go options with increased account limits, expanded file vault storage, and extended message retention",
        status: "coming-soon",
      },
    ],
  },
  ecosystem: {
    title: "Part of the Majikah Ecosystem",
    paragraphs: [
      "Majik Message is a flagship product within the Majikah system—a suite of privacy-focused, user-controlled tools designed to give individuals full ownership of their digital communications and data.",
      "All Majikah products share the same core principles: cryptographic identity, zero-knowledge architecture, post-quantum security, and user sovereignty over personal information.",
    ],
  },
  cta: {
    title: "Ready to Take Control of Your Messages?",
    description:
      "Download Majik Message and experience post-quantum security with voice calls, encrypted files, and digital signatures:",
    downloads: [
      {
        platform: "GitHub Releases",
        url: "https://github.com/Majikah/majik-message/releases",
      },
      {
        platform: "Microsoft Store",
        url: "https://apps.microsoft.com/detail/9PMJGVZZJSPN",
      },
      {
        platform: "Web App",
        url: "https://message.majikah.solutions",
      },
      {
        platform: "Chrome Extension",
        url: "https://chromewebstore.google.com/detail/majik-message/dhlafmkpgjagkhiokoighjaakajbckck",
      },
    ],
    closingStatement: "Quantum-safe private messaging, redefined.",
  },
};
